export interface NotificationStreamEvent {
  id: string;
  kind: string;
  title?: string;
  message?: string;
  link?: string;
  in_bell?: boolean;
  payload?: Record<string, unknown>;
  created_at: number;
}

export class NotificationStreamHTTPError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'NotificationStreamHTTPError';
    this.status = status;
  }
}

interface OpenNotificationStreamOptions {
  token: string;
  signal: AbortSignal;
  onOpen?: () => void;
  onNotification: (event: NotificationStreamEvent) => void;
}

const streamEndpoint = '/api/notification/stream';
const sseNotificationEvent = 'notification';

export async function openNotificationStream({
  token,
  signal,
  onOpen,
  onNotification,
}: OpenNotificationStreamOptions): Promise<void> {
  const response = await fetch(streamEndpoint, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
    },
    cache: 'no-store',
    signal,
  });

  if (!response.ok) {
    throw new NotificationStreamHTTPError(
      response.status,
      `notification stream request failed: ${response.status}`,
    );
  }

  if (!response.body) {
    throw new Error('notification stream response body is not available');
  }

  onOpen?.();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) {
        break;
      }

      buffer += decoder.decode(chunk.value, { stream: true });
      buffer = drainParsedEvents(buffer, onNotification);
    }
  } finally {
    reader.releaseLock();
  }

  const tail = decoder.decode();
  if (tail) {
    buffer += tail;
    drainParsedEvents(buffer, onNotification);
  }
}

function drainParsedEvents(
  rawBuffer: string,
  onNotification: (event: NotificationStreamEvent) => void,
): string {
  let buffer = rawBuffer;

  for (;;) {
    const eventBoundaryIndex = findEventBoundary(buffer);
    if (eventBoundaryIndex < 0) {
      return buffer;
    }

    const rawEvent = buffer.slice(0, eventBoundaryIndex);
    const separatorLength = buffer.startsWith('\r\n\r\n', eventBoundaryIndex) ? 4 : 2;
    buffer = buffer.slice(eventBoundaryIndex + separatorLength);

    const parsed = parseSSEEvent(rawEvent);
    if (parsed === null || parsed.eventName !== sseNotificationEvent || parsed.data === '') {
      continue;
    }

    try {
      const payload = JSON.parse(parsed.data) as NotificationStreamEvent;
      onNotification(payload);
    } catch {
      // Ignore malformed payloads to preserve stream processing for subsequent events.
    }
  }
}

function findEventBoundary(buffer: string): number {
  const lfBoundary = buffer.indexOf('\n\n');
  const crlfBoundary = buffer.indexOf('\r\n\r\n');

  if (lfBoundary < 0) {
    return crlfBoundary;
  }

  if (crlfBoundary < 0) {
    return lfBoundary;
  }

  return Math.min(lfBoundary, crlfBoundary);
}

function parseSSEEvent(rawEvent: string): { eventName: string; data: string } | null {
  if (!rawEvent) {
    return null;
  }

  const lines = rawEvent.replace(/\r/g, '').split('\n');
  let eventName = 'message';
  const dataParts: string[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (line === '' || line.startsWith(':')) {
      continue;
    }

    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
      continue;
    }

    if (line.startsWith('data:')) {
      dataParts.push(line.slice('data:'.length).trimStart());
    }
  }

  return {
    eventName,
    data: dataParts.join('\n'),
  };
}
